import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { ShieldCheck, ArrowLeft, Lock } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { createEmbeddedCheckoutSession } from "../lib/api";

// Load Stripe.js only once at module level (Stripe recommendation)
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : Promise.reject(new Error("Missing REACT_APP_STRIPE_PUBLISHABLE_KEY"));

export default function EmbeddedCheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);

  // Reset scroll on page mount (react-router keeps scrollY across route changes by default)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Order recap comes from CheckoutModal via location.state
  const order = location.state?.order || null;

  // Fetch client_secret via callback (Stripe pattern)
  const fetchClientSecret = useCallback(async () => {
    if (!order) throw new Error("Ordine non trovato — torna alla pagina corso.");
    try {
      const res = await createEmbeddedCheckoutSession({
        productId: order.productId,
        customerEmail: order.customerEmail,
        customerName: order.customerName,
        customerPhone: order.customerPhone,
        billingAddress: order.billingAddress,
        billingCity: order.billingCity,
        billingZip: order.billingZip,
        billingProvince: order.billingProvince,
        billingCountry: order.billingCountry,
        wantsInvoice: order.wantsInvoice,
        codiceFiscale: order.codiceFiscale,
        ragioneSociale: order.ragioneSociale,
        partitaIva: order.partitaIva,
        codiceSdi: order.codiceSdi,
        pecEmail: order.pecEmail,
        discountCode: order.discountCode,
      });
      setClientSecret(res.client_secret);
      return res.client_secret;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore nella creazione del pagamento. Riprova.";
      setError(detail);
      throw e;
    }
  }, [order]);

  useEffect(() => {
    if (!order) {
      setError("Nessun ordine attivo — torna alla pagina corso e ripeti la procedura di iscrizione.");
    }
  }, [order]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navbar />

      <main className="container-max pt-28 pb-20 md:pt-32 md:pb-24">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
            data-testid="checkout-back-btn"
          >
            <ArrowLeft size={14} /> Torna al corso
          </button>
        </div>

        <div className="mb-10">
          <div className="font-mono-label text-white/40 text-[10px] tracking-widest mb-2">
            CHECKOUT SICURO
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05] font-semibold tracking-tight">
            Completa il tuo <span style={{ color: "#d4ff3d" }}>ordine</span>
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-8">
          {/* Order Recap */}
          <aside className="lg:sticky lg:top-28 self-start">
            {order ? (
              <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6 md:p-7">
                <div className="font-mono-label text-white/40 text-[10px] mb-3">Riepilogo ordine</div>
                <h2 className="font-display text-white text-2xl md:text-[26px] leading-tight tracking-tight mb-4">
                  {order.productName}
                </h2>
                <div className="border-t border-white/8 pt-4 space-y-3 text-sm">
                  <Row label="Iscritto" value={order.customerName} />
                  <Row label="Email" value={order.customerEmail} />
                  {order.customerPhone && <Row label="Telefono" value={order.customerPhone} />}
                  {order.billingAddress && (
                    <Row
                      label="Indirizzo"
                      value={`${order.billingAddress}, ${order.billingZip} ${order.billingCity} (${order.billingProvince}) ${order.billingCountry}`}
                    />
                  )}
                  {order.wantsInvoice ? (
                    <>
                      {order.ragioneSociale && <Row label="Ragione sociale" value={order.ragioneSociale} />}
                      {order.partitaIva && <Row label="P.IVA" value={order.partitaIva} />}
                      {order.codiceSdi && <Row label="Codice SDI" value={order.codiceSdi} />}
                      {order.pecEmail && <Row label="PEC" value={order.pecEmail} />}
                    </>
                  ) : (
                    order.codiceFiscale && <Row label="Codice fiscale" value={order.codiceFiscale} />
                  )}
                  <div className="border-t border-white/8 pt-4 mt-4 flex items-center justify-between">
                    <span className="text-white/60 text-sm">Totale</span>
                    <span className="font-display text-white text-3xl">
                      &euro;{Number(order.productPrice).toFixed(0)}
                    </span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/8 space-y-3 text-xs text-white/50">
                  <div className="flex items-start gap-2">
                    <Lock size={13} style={{ color: "#d4ff3d" }} className="mt-0.5 shrink-0" />
                    <span>I dati della carta viaggiano cifrati direttamente a Stripe. Il nostro server non li vede mai.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <ShieldCheck size={13} style={{ color: "#d4ff3d" }} className="mt-0.5 shrink-0" />
                    <span>Pagamento certificato PCI DSS Level 1. Rimborso garantito 14 giorni dall&apos;acquisto.</span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/8">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Metodi accettati</div>
                  <div className="flex flex-wrap gap-2 text-[11px] text-white/70">
                    <Chip>Carta</Chip>
                    <Chip>PayPal</Chip>
                    <Chip>Apple / Google Pay</Chip>
                  </div>
                  <div className="text-[10px] text-white/40 mt-2 leading-relaxed">
                    Metodi effettivamente disponibili in base al tuo Paese, browser e dispositivo.
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-[#0f0f0f] border border-red-400/30 rounded-2xl p-6 text-red-300 text-sm">
                {error || "Ordine non disponibile."}
                <div className="mt-4">
                  <Link to="/" className="btn-primary inline-flex" data-testid="checkout-error-home-btn">
                    Torna alla home
                  </Link>
                </div>
              </div>
            )}
          </aside>

          {/* Stripe Embedded Checkout */}
          {order && error ? (
            <section className="bg-[#0f0f0f] border border-red-400/30 rounded-2xl p-8 min-h-[240px] flex flex-col items-start justify-center gap-4" data-testid="checkout-error-msg">
              <div className="text-red-300 text-sm leading-relaxed">{error}</div>
              {/\bsconto\b/i.test(error) && (
                <div className="text-white/50 text-xs">Controlla di aver scritto il codice esattamente come nell&apos;email (o rimuovilo per procedere a prezzo pieno).</div>
              )}
              <button
                onClick={() => navigate(-1)}
                className="btn-primary inline-flex"
                data-testid="checkout-error-back-btn"
              >
                Torna indietro e correggi
              </button>
            </section>
          ) : order ? (
            <section className="bg-white rounded-2xl p-2 md:p-4 min-h-[600px]" data-testid="embedded-checkout-container">
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </section>
          ) : (
            <section className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-8 text-white/60 text-sm min-h-[240px] flex items-center">
              Torna alla pagina del corso e ripeti l&apos;iscrizione per completare il pagamento.
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-white/50 text-[13px]">{label}</span>
      <span className="text-white text-[13px] text-right break-all">{value}</span>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.02]">
      {children}
    </span>
  );
}
